import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  type NWTEvent,
  useTracking,
} from '@xing-com/crate-communication-tracking';
import { useLocalStorage } from '@xing-com/crate-hooks-use-web-storage';
import { useSixtFocusmode } from '@xing-com/crate-layout-use-sixt-focusmode';
import { useViewerData } from '@xing-com/hub';
import { TipBoxPreset } from '@xing-com/tip-box';
import { Toggle } from '@xing-com/toggle';
import { spaceS } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { UpdateSixtFocusmodeStateDocument } from './sixt-focusmode-state.gql-types';

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spaceS};
  align-items: center;
`;

export const SixtFocusmodeToggle: React.FC = () => {
  const { formatMessage } = useIntl();
  const userId = useViewerData()?.data?.user?.id;
  const { track } = useTracking<NWTEvent>();
  const { isSixtFocusmodeEnabled, isSixtFocusmodeState } = useSixtFocusmode();

  const [isFirstTime, setIsFirstTime] = useLocalStorage(
    'sixt-focusmode-tip-box-first-time',
    true
  );

  const [mutate, { loading: mutationLoading }] = useMutation(
    UpdateSixtFocusmodeStateDocument,
    {
      update: (cache, { data }) => {
        cache.modify({
          id: cache.identify({ __typename: 'Viewer', id: userId }),
          fields: {
            coreSettings(existingCoreSettings = {}) {
              return {
                ...existingCoreSettings,
                sixtFocusmodeState:
                  data?.updateCoreSettings?.success?.sixtFocusmodeState,
              };
            },
          },
        });
      },
      onCompleted: (data) => {
        const value =
          data.updateCoreSettings?.success?.sixtFocusmodeState?.value;

        if (value !== undefined) {
          track({
            type: 'nwt',
            event: value ? 'activated' : 'deactivated',
            eventSchema: 'basic',
            sentBy: 'navigation',
            element: 'sixt_focusmode',
            elementDetail: 'toggle',
          });
        }
      },
    }
  );

  const [showTipBox, setShowTipBox] = useState(isFirstTime);

  const handleToggleChange = (): void => {
    setShowTipBox(false);

    mutate({
      variables: {
        input: {
          sixtFocusmodeState: {
            value: !isSixtFocusmodeState,
          },
        },
      },
    });
  };

  const onOpenChange = (open: boolean): void => {
    setIsFirstTime(false);

    if (!open) {
      setShowTipBox(false);
    }
  };

  const onClickTipbox = (): void => {
    setIsFirstTime(false);

    handleToggleChange();
  };

  const onDisableAutofocus = (e: Event): void => {
    e.preventDefault();
  };

  return (
    <ToggleWrapper>
      <Toggle
        disabled={mutationLoading}
        on={isSixtFocusmodeEnabled}
        data-testId="sixt-focusmode-toggle"
        onChange={handleToggleChange}
      />
      <BodyCopy size="small" noMargin>
        {formatMessage({
          id: 'MAINNAV_JOB_FOCUS_TOGGLE',
        })}
      </BodyCopy>
      <TipBoxPreset
        align="end"
        bodyText={formatMessage({
          id: isSixtFocusmodeState
            ? 'TIPBOX_JOB_FOCUS_ON_DESCRIPTION'
            : 'TIPBOX_JOB_FOCUS_DESCRIPTION',
        })}
        buttonLabel={formatMessage({
          id: isSixtFocusmodeState
            ? 'TIPBOX_JOB_FOCUS_ON_BUTTON_LABEL'
            : 'TIPBOX_JOB_FOCUS_BUTTON_LABEL',
        })}
        headlineText={formatMessage({
          id: isSixtFocusmodeState
            ? 'TIPBOX_JOB_FOCUS_ON_TITLE'
            : 'TIPBOX_JOB_FOCUS_TITLE',
        })}
        {...(isSixtFocusmodeState
          ? {}
          : {
              markerText: formatMessage({
                id: 'TIPBOX_JOB_FOCUS_MARKER',
              }),
            })}
        showArrow
        open={showTipBox}
        onOpenChange={onOpenChange}
        onOpenAutoFocus={onDisableAutofocus}
        buttonProps={{
          onClick: onClickTipbox,
        }}
        showClosingIndicator
        side="bottom"
        variant={isSixtFocusmodeState ? 'default' : 'highlighted'}
        trigger={<div></div>}
      />
    </ToggleWrapper>
  );
};
